import { useEffect, useState } from "react";
import { Hourglass } from "lucide-react";
import { ChapterHead, Reveal } from "./motion";
import { OFFER, TERMS, ASSUMPTIONS, EXCLUSIONS } from "./data";

const useCountdown = () => {
  const calc = () => {
    const diff = new Date(OFFER.validUntilISO) - new Date();
    return Math.max(0, Math.ceil(diff / 86400000));
  };
  const [days, setDays] = useState(calc);
  useEffect(() => {
    const t = setInterval(() => setDays(calc()), 60000);
    return () => clearInterval(t);
  }, []);
  return days;
};

export const Terms = () => {
  const days = useCountdown();
  return (
    <section data-testid="chapter-terms" className="chapter-print-break mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
      <ChapterHead
        num="07"
        title="Terms, Assumptions & Boundaries"
        sub="Clear scope boundaries make the schedule and risk profile predictable. Each assumption is validated during Phase 0 discovery."
      />
      <Reveal className="mb-14">
        <div className="flex flex-wrap items-center gap-8 rounded-sm border border-amber-500/30 bg-surface px-8 py-8 sm:px-12">
          <Hourglass className="h-10 w-10 text-amber-500" />
          <div>
            <p data-testid="validity-countdown" className="font-display text-4xl font-extrabold text-ink sm:text-5xl">
              {days} <span className="text-xl font-semibold text-accent">days</span>
            </p>
            <p className="mt-2 text-sm text-ink4">
              remaining to accept this offer — valid until {OFFER.validUntil}
            </p>
          </div>
        </div>
      </Reveal>

      <div className="grid gap-x-14 gap-y-10 sm:grid-cols-3">
        {TERMS.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.06} data-testid={`term-item-${i}`}>
            <div className="border-t border-line pt-6">
              <p className="font-mono2 mb-2 text-[11px] tracking-[0.3em] text-accent uppercase">
                § {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="font-display text-lg font-semibold text-ink">{t.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink4">{t.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="mt-16 grid gap-6 lg:grid-cols-2">
        <Reveal>
          <div className="h-full rounded-sm border border-line bg-surface p-8">
            <p className="font-mono2 mb-6 text-[11px] tracking-[0.3em] text-accent uppercase">Assumptions</p>
            <ul className="space-y-5">
              {ASSUMPTIONS.map((a) => (
                <li key={a.id} data-testid={`assumption-${a.id.toLowerCase()}`} className="flex gap-4">
                  <span className="font-mono2 shrink-0 text-xs font-semibold text-accent">{a.id}</span>
                  <p className="text-sm leading-relaxed text-ink3">{a.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="h-full rounded-sm border border-line bg-surface p-8">
            <p className="font-mono2 mb-6 text-[11px] tracking-[0.3em] text-ink5 uppercase">Exclusions</p>
            <ul className="space-y-5">
              {EXCLUSIONS.map((e) => (
                <li key={e.id} data-testid={`exclusion-${e.id.toLowerCase()}`} className="flex gap-4">
                  <span className="font-mono2 shrink-0 text-xs font-semibold text-ink5">{e.id}</span>
                  <p className="text-sm leading-relaxed text-ink4">{e.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
